import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Info } from 'lucide-react';
import { isAxiosError } from 'axios';
import { invite } from '../services/auth.service';
import { useRoles } from '@/features/administration/hooks/use-roles.hook';
import { extractServerErrors } from '@/lib/api/error';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const NO_ROLE = '__none__';

const schema = z.object({
  email: z.string().email('Please enter a valid email address'),
  roleHint: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface InviteUserFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function InviteUserForm({ onSuccess, onCancel }: InviteUserFormProps) {
  const { data: roles, isLoading: rolesLoading } = useRoles();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', roleHint: NO_ROLE },
  });

  const inviteMutation = useMutation({
    mutationFn: ({ email, roleHint }: { email: string; roleHint?: string }) =>
      invite(email, roleHint),
    onSuccess: () => {
      toast.success('Invitation sent.');
      form.reset();
      onSuccess?.();
    },
    onError: (err) => {
      if (isAxiosError(err) && err.response) {
        const msgs = extractServerErrors(err);
        form.setError('root', { message: msgs[0] ?? 'Failed to send invitation.' });
      } else {
        toast.error('Network error. Please try again.');
      }
    },
  });

  function onSubmit(values: FormValues) {
    const roleHint =
      values.roleHint && values.roleHint !== NO_ROLE ? values.roleHint : undefined;
    inviteMutation.mutate({ email: values.email, roleHint });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email address</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  autoComplete="email"
                  autoFocus
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="roleHint"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Initial role (optional)</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={rolesLoading}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={rolesLoading ? 'Loading roles…' : 'No role'} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={NO_ROLE}>No role</SelectItem>
                  {roles?.map((r) => (
                    <SelectItem key={r.id} value={r.name}>
                      {r.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Additional roles can be assigned in user management after the invitation is
            accepted.
          </span>
        </div>

        {form.formState.errors.root && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {form.formState.errors.root.message}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={inviteMutation.isPending}>
            {inviteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {inviteMutation.isPending ? 'Sending…' : 'Send invitation'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
